import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Box, Paper, Typography, Grid, Card, CardContent, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Button
} from '@mui/material';
import { LocationOn, Business } from '@mui/icons-material';
import USMapVisualization from './USMapVisualization';
import apiClient from '../utils/axiosConfig';

// State name mapping
const STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

function GeographicDashboard() {
  const [geographicData, setGeographicData] = useState({});
  const [metrics, setMetrics] = useState({ totalActiveAccounts: 0, accountsWithoutState: 0, statesWithAccounts: 0 });
  const [selectedState, setSelectedState] = useState(null);
  const [stateAccounts, setStateAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const fetchGeographicData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiClient.get('/api/accounts/geographic-distribution');
      
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      setGeographicData(response.data.distribution || {});
      setMetrics(response.data.metrics || { totalActiveAccounts: 0, accountsWithoutState: 0, statesWithAccounts: 0 });
      setRetryCount(0);
    } catch (error) {
      const errorMessage = error.response?.status === 401 ? 'Authentication required' : 'Failed to load geographic data';
      setError(errorMessage);
      
      // Auto-retry on network errors (max 3 times)
      if (retryCount < 3 && !error.response?.status) {
        setTimeout(() => {
          setRetryCount(prev => prev + 1);
          fetchGeographicData();
        }, 2000 * (retryCount + 1));
      }
    } finally {
      setLoading(false);
    }
  }, [retryCount]);

  const handleStateClick = useCallback(async (stateCode) => {
    if (!stateCode || !geographicData[stateCode]) return;
    
    try {
      setSelectedState(stateCode);
      setError('');
      const response = await apiClient.get(`/api/accounts/by-state/${stateCode}`);
      setStateAccounts(response.data || []);
    } catch (error) {
      const errorMessage = error.response?.status === 401 ? 'Authentication required' : 'Failed to load state accounts';
      setError(errorMessage);
      setStateAccounts([]);
    }
  }, [geographicData]);

  useEffect(() => {
    fetchGeographicData();
  }, [fetchGeographicData]);

  const topStates = useMemo(() => {
    return Object.entries(geographicData)
      .filter(([state, count]) => state !== 'UNKNOWN' && count > 0)
      .sort(([,a], [,b]) => b - a);
  }, [geographicData]);

  const topState = useMemo(() => {
    return topStates.length > 0 ? topStates[0] : null;
  }, [topStates]);

  const allStatesWithAccounts = useMemo(() => {
    return Object.entries(geographicData)
      .filter(([state, count]) => state !== 'UNKNOWN' && count > 0)
      .sort(([,a], [,b]) => b - a);
  }, [geographicData]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert 
        severity="error" 
        sx={{ mb: 3 }}
        action={
          <Button 
            color="inherit" 
            size="small" 
            onClick={fetchGeographicData}
            disabled={loading}
          >
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: 'primary.50',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Business color="primary" sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      mb: 0.5
                    }}>
                    Active Accounts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {metrics.totalActiveAccounts}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: 'secondary.50',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocationOn color="secondary" sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      mb: 0.5
                    }}>
                    States with Accounts
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {metrics.statesWithAccounts}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: 'success.50',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocationOn color="success" sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      mb: 0.5
                    }}>
                    Top State
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {topState ? `${STATE_NAMES[topState[0]] || topState[0]} (${topState[1]})` : 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ 
            height: '100%',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              transform: 'translateY(-2px)'
            }
          }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  backgroundColor: 'warning.50',
                  mr: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <LocationOn color="warning" sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      mb: 0.5
                    }}>
                    Accounts Without State
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {metrics.accountsWithoutState}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Map and Tables */}
      <Grid container spacing={3}>
        {/* US Map */}
        <Grid
          size={{
            xs: 12,
            lg: 8
          }}>
          <Paper sx={{ p: 3, height: 700 }}>
            <Typography variant="h6" gutterBottom>
              Regional Account Distribution
            </Typography>
            <USMapVisualization 
              data={geographicData}
              onStateClick={handleStateClick}
              selectedState={selectedState}
              showLegend={false}
            />
          </Paper>
        </Grid>

        {/* Top States Table */}
        <Grid
          size={{
            xs: 12,
            lg: 4
          }}>
          <Paper sx={{ p: 3, height: 700, overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Top States by Account Count
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell align="right">Accounts</TableCell>
                    <TableCell align="right">%</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allStatesWithAccounts.map(([state, count]) => (
                    <TableRow 
                      key={state}
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedState === state ? 'action.selected' : 'inherit'
                      }}
                      onClick={() => handleStateClick(state)}
                    >
                      <TableCell component="th" scope="row">
                        {STATE_NAMES[state] || state}
                      </TableCell>
                      <TableCell align="right">{count}</TableCell>
                      <TableCell align="right">
                        {metrics.totalActiveAccounts > 0 ? ((count / metrics.totalActiveAccounts) * 100).toFixed(1) : '0.0'}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Legend - Outside the map container */}
      <Box sx={{ mt: 3 }}>
        <Paper sx={{ 
          p: 2, 
          backgroundColor: '#fafafa', 
          border: '1px solid #e0e0e0',
          borderRadius: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Account Distribution:
            </Typography>
            {[
              { range: '0', color: '#f8f9fa', label: 'No accounts' },
              { range: '1-5', color: '#e8f5e8', label: '1-5 accounts' },
              { range: '6-15', color: '#c8e6c8', label: '6-15 accounts' },
              { range: '16-30', color: '#81c784', label: '16-30 accounts' },
              { range: '30+', color: '#4caf50', label: '30+ accounts' }
            ].map((item) => (
              <Box key={item.range} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    backgroundColor: item.color,
                    border: '1px solid #ccc',
                    borderRadius: 0.5,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  {item.range}
                </Typography>
              </Box>
            ))}
          </Box>
          
          {/* Instructions */}
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontSize: '0.8rem',
              fontStyle: 'italic'
            }}>
            💡 Hover over states to see account details • Click states with accounts for detailed information
          </Typography>
        </Paper>
      </Box>

    </Box>
  );
}

export default GeographicDashboard;